import React, { useEffect, useState } from "react";
import "./Career.css";
import { FaMapMarkerAlt, FaBriefcase } from "react-icons/fa";
import { motion } from "framer-motion";
import { getJobs } from "../services/jobService";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const storage = getStorage();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    experience: "",
    location: "",
    linkedin: "",
    message: "",
    resume: null,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setJobsLoading(true);
        setJobsError("");

        const companyId = "MPSL520209"; // 🔥 apna companyId
        const jobsList = await getJobs(companyId);

        console.log("Fetched Jobs:", jobsList);

        setJobs(jobsList);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobsError(
          "Unable to load job postings right now. Check Firestore data."
        );
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const filteredJobs = jobs.filter((job) =>
    (job.title || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleApply = (job) => {
    setSelectedJob({ ...job });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "fullName") {
      if (/[^a-zA-Z\s]/.test(value)) return;

      const capitalized = value
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      setFormData({ ...formData, fullName: capitalized });
      setErrors({ ...errors, fullName: "" });
      return;
    }

    if (name === "phone") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;

      setFormData({ ...formData, phone: value });
      setErrors({ ...errors, phone: "" });
      return;
    }

    if (name === "email") {
      let errorMsg = "";
      const validDomains = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com"];

      if (!value) {
        errorMsg = "Enter your email";
      } else if (!/^\S+@\S+\.\S+$/.test(value)) {
        errorMsg = "Enter valid email";
      } else {
        const domain = value.split("@")[1];
        if (!validDomains.includes(domain)) {
          errorMsg = "Enter valid email domain";
        }
      }

      setErrors({ ...errors, email: errorMsg });
    }

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, resume: file });
    setErrors({ ...errors, resume: "" });

    if (file) {
      setIsActive(true);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName) newErrors.fullName = "Enter full name";

    if (!formData.email) {
      newErrors.email = "Enter email";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email";
    }

    if (!formData.phone) newErrors.phone = "Enter phone";
    if (!formData.location) newErrors.location = "Enter location";
    if (!formData.experience) newErrors.experience = "Enter your experience";
    if (!formData.resume) newErrors.resume = "Upload resume";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    let errorMsg = "";
    const validDomains = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com"];

    setTouched({ ...touched, [name]: true });

    if (name === "fullName" && !formData.fullName) {
      errorMsg = "Enter your full name";
    }

    if (name === "email") {
      if (!formData.email) {
        errorMsg = "Enter your email";
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        errorMsg = "Enter valid email";
      } else {
        const domain = formData.email.split("@")[1];
        if (!validDomains.includes(domain)) {
          errorMsg = "Enter valid email domain";
        }
      }
    }

    if (name === "phone" && !formData.phone) {
      errorMsg = "Enter your phone number";
    }

    if (name === "location" && !formData.location) {
      errorMsg = "Enter your location";
    }

    if (name === "experience" && !formData.experience) {
      errorMsg = "Enter your experience";
    }

    setErrors({ ...errors, [name]: errorMsg });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      let resumeURL = "";

      // ================= UPLOAD TO STORAGE =================
      if (formData.resume) {
        const storageRef = ref(
          storage,
          `resumes/${Date.now()}_${formData.resume.name}`
        );

        const snapshot = await uploadBytes(storageRef, formData.resume);
        resumeURL = await getDownloadURL(snapshot.ref);
      }

      // ================= SAVE TO FIRESTORE =================
      await addDoc(collection(db, "ats_applications"), {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        experience: formData.experience,
        location: formData.location,
        linkedin: formData.linkedin || "",
        message: formData.message || "",
        jobTitle: selectedJob?.title,
        jobCode: selectedJob?.code,
        companyId: selectedJob?.companyId,
        status: "Applied",
        resumeURL: resumeURL, // 🔥 SAVE FILE LINK
        createdAt: new Date()
      });

      // ================= BACKEND MAIL =================
      const form = new FormData();
      form.append("fullName", formData.fullName);
      form.append("email", formData.email);
      form.append("phone", formData.phone);
      form.append("experience", formData.experience);
      form.append("location", formData.location);
      form.append("linkedin", formData.linkedin);
      form.append("message", formData.message);
      form.append("resume", formData.resume);

      form.append("jobTitle", selectedJob?.title);
      form.append("jobCode", selectedJob?.code);

      await fetch("https://themps.global/api/career-mail", {
        method: "POST",
        body: form,
      });

      alert("Application Submitted ✅");

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        experience: "",
        location: "",
        linkedin: "",
        message: "",
        resume: null,
      });

      setShowModal(false);

    } catch (err) {
      console.error(err);
      alert("Error ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="site-header site-news-header">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 col-12 mx-auto">
              <h1 className="text-white">We Are Hiring!</h1>
              <strong className="text-white">
                Our Company Needs Fresh Minds!
              </strong>
            </div>
          </div>
        </div>

        <div className="video-wrap">
          <video autoPlay loop muted className="custom-video">
            <source src="video/career_tiny.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="overlay"></div>
      </header>

      <section className="career-section">
        <h2>Join Our Growing Team</h2>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by job title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button">Search</button>
        </div>

        {jobsLoading && <p className="jobs-status">Loading job openings...</p>}
        {jobsError && <p className="jobs-status jobs-status-error">{jobsError}</p>}
        {!jobsLoading && !jobsError && filteredJobs.length === 0 && (
          <p className="jobs-status">No job postings found.</p>
        )}

        <div className="job-grid">
          {filteredJobs.map((job, i) => (
            <motion.div
              className="job-card-new"
              key={job.id || i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="job-title">{job.title}</h3>

              <div className="job-info">
                <span>
                  <FaMapMarkerAlt className="icon" /> {job.location}
                </span>

                <span>
                  <FaBriefcase className="icon" /> {job.exp}
                </span>
              </div>

              <div className="job-code-row">
                <span>Job Code: {job.code}</span>
              </div>

              <div className="job-bottom">
                <div className="exp-box">
                  <small>Experience</small>
                  <strong>{job.exp}</strong>
                </div>

                <button
                  className="apply-btn-new"
                  onClick={() => handleApply(job)}
                >
                  Apply Now
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {showModal && (
        <div className="modal-overlayout">
          <div className="modal-box-new">
            <span className="close-btn" onClick={closeModal}>
              X
            </span>

            <p className="top-small">CAREER APPLICATION</p>
            <h2>Apply for This Opportunity</h2>

            <p className="sub-test">
              Join Maa Pranaam Suvidha Limited and build your career with a team
              focused on growth, professionalism, and long-term impact.
            </p>

            <div className="job-header-box">
              <h3>{selectedJob?.title}</h3>

              <div className="job-meta">
                <span>{selectedJob?.location}</span>
                <span>{selectedJob?.exp}</span>
                <span>Job Code: {selectedJob?.code}</span>
              </div>
            </div>

            <form className="apply-form-new" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your full name"
                />
                {errors.fullName && <small className="error">{errors.fullName}</small>}
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your email"
                />
                {errors.email && <small className="error">{errors.email}</small>}
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter phone number"
                />
                {errors.phone && <small className="error">{errors.phone}</small>}
              </div>

              <div className="form-group">
                <label>Experience</label>
                <input
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your experience"
                />
                {errors.experience && (
                  <small className="error">{errors.experience}</small>
                )}
              </div>

              <div className="form-group">
                <label>Current Location *</label>
                <input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your location"
                />
                {errors.location && <small className="error">{errors.location}</small>}
              </div>

              <div className="form-group">
                <label>LinkedIn Profile</label>
                <input
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  placeholder="Paste LinkedIn URL"
                />
              </div>

              <div className="form-group full">
                <label>Cover Letter / Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message"
                ></textarea>
              </div>

              <div className="form-group full">
                <label>Upload Resume *</label>
                <div
                  className={`upload-box ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setIsActive(true);
                    document.getElementById("resumeUpload").click();
                  }}
                >
                  <p>Drag & drop or click to upload</p>
                  <small>PDF/DOC up to 2MB</small>

                  {formData.resume && (
    <p className="file-name">{formData.resume.name}</p>
  )}

                  <input
                    type="file"
                    id="resumeUpload"
                    name="resume"
                    style={{ display: "none" }}
                    onChange={handleFile}
                  />
                </div>
                {errors.resume && <small className="error">{errors.resume}</small>}
              </div>

              <p className="note full">
                Your resume will be analyzed automatically to assess compatibility.
              </p>

              <button
                className="submit-btn full"
                disabled={
                  loading ||
                  !formData.fullName ||
                  !formData.email ||
                  !formData.phone ||
                  !formData.location ||
                  !formData.experience ||
                  !formData.resume
                }
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Career;
